import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useTable, useGlobalFilter, useSortBy, usePagination } from "react-table";
import { useAuth0 } from "@auth0/auth0-react";

import { GetFarmDetails, GetDecisions, AddDecision, UpdateDecision, DeleteDecision, useCheckScan, useCheckDecision } from "api/farm";

import DecisionProfile from "components/Miners/DecisionProfile";
import DecisionModal from "components/Miners/DecisionModal";
import Pagination from "components/Util/Pagination";
import Modal from "components/Util/Modal";
import NoAccess from "components/Util/NoAccess";
import Spinner from "components/svg/Spinner";

export default function Decisions() {
  const { user } = useAuth0();

  const [selectedRows, setSelectedRows] = useState([]);
  const [farmUrl, setFarmUrl] = useState("");
  const [farmKey, setFarmKey] = useState("");
  const [isDecisionModalOpen, setIsDecisionModalOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [decisions, setDecisions] = useState([]);
  const [refetchInterval, setRefetchInterval] = useState(0);
  const [decisionInterval, setDecisionInterval] = useState(0);

  const emptyDecision = { id: "", name: "" };

  const [selectedDecision, setSelectedDecision] = useState(emptyDecision);

  const [farmName] = useOutletContext<any>();

  const updateDecisions = useCallback(() => {
    if (farmUrl !== "" && farmUrl !== null && farmKey !== "") {
      GetDecisions(farmUrl, farmKey).then((data: any) => setDecisions(data));
    }
  }, [farmUrl, farmKey]);

  useEffect(() => {
    if (farmName !== "") {
      GetFarmDetails(user["https://hasura.io/jwt/claims"]["x-hasura-user-id"]).then((details: any) => {
        if (typeof details !== "undefined") {
          setFarmUrl(details.farm_url);
          setFarmKey(details.farm_token);
        }
      });
    }

    updateDecisions();
  }, [user, farmName, updateDecisions]);

  const { data: scanStatus } = useCheckScan(farmUrl, farmKey, refetchInterval);
  const { data: decisionStatus } = useCheckDecision(farmUrl, farmKey, decisionInterval);

  useEffect(() => {
    if (refetchInterval !== 10 && (scanStatus === "scan complete" || scanStatus === "please start a scan") && decisionStatus === "ready") {
      setRefetchInterval(30000);
      setDecisionInterval(30000);
      document.body.classList.remove("disable-settings");
    }
    else if (scanStatus === "loading" || decisionStatus === "loading") {
      setRefetchInterval(1000);
      setDecisionInterval(1000);
      document.body.classList.add("disable-settings");
    }
    else if (refetchInterval !== 10) {
      setRefetchInterval(5000);
      setDecisionInterval(5000);
      document.body.classList.add("disable-settings");
    }
  }, [scanStatus, refetchInterval, decisionStatus]);

  const closeDecisionModal = () => {
    setIsDecisionModalOpen(false);
  };

  const openDecisionModal = () => {
    setIsDecisionModalOpen(true);
  };

  const handleSubmitDecision = (decision: any) => {
    if (decision.id === "") {
      AddDecision(farmUrl, farmKey, decision.name)
        .then(updateDecisions);
    }
    else {
      UpdateDecision(farmUrl, farmKey, decision.id, decision.name).then(updateDecisions);
    }
    closeDecisionModal();
    setSelectedDecision(emptyDecision);
  };

  const toggleSelect = useCallback(
    (rowData: any, selectAll?: boolean) => {
      if (selectAll && selectedRows.length) {
        setSelectedRows([]);
      } else if (selectAll) {
        setSelectedRows(rowData);
      } else {
        let foundIndex = selectedRows.findIndex((row) => row.ip === rowData.ip);

        if (foundIndex !== -1) {
          const newSelectedRows = [...selectedRows];
          newSelectedRows.splice(foundIndex, 1);
          setSelectedRows(newSelectedRows);
        } else {
          setSelectedRows([...selectedRows, rowData]);
        }
      }
    },
    [selectedRows]
  );

  const columns = useMemo<any[]>(
    () => [
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "",
        accessor: "id",
        id: "action",
        align: "center",
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    setGlobalFilter,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
  }: any = useTable(
    {
      columns,
      data: decisions,
      initialState: {
        pageSize: 10,
        sortBy: [{ id: "id", desc: false }],
      },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <div className="p-6">
      {(farmUrl === "") || (farmUrl === null) ?
        <NoAccess />
        :
        <section className="pt-5 sm:overflow-hidden">
          <div className="flex border-b pb-3 justify-between items-end mb-1 border-gray-200">
            <input
              type="search"
              className="form-input px-3 border-solid placeholder-gray-300 border-gray-200 rounded-sm text-sm py-2 w-56 focus:border-transparent focus:ring-transparent"
              placeholder="Search"
              value={globalFilter || ""}
              onChange={(e) => {
                setGlobalFilter(e.target.value || undefined);
              }}
            />
            <div className="flex items-center">
              <div className="show-spinner">
                <button
                  type="button"
                  className="form-input inline-flex items-center border border-transparent leading-4 font-medium shadow-sm text-white focus:outline-none bg-blue-600 hover:bg-blue-700 rounded-sm px-4 py-2 text-sm"
                  onClick={() => {
                    if (selectedDecision.id !== "") {
                      setSelectedDecision(emptyDecision);
                    }

                    openDecisionModal();
                  }}
                >
                  Add Profile
                </button>
                <Spinner className="block mx-auto h-5 w-5 text-gray-400" />
              </div>
              <DecisionModal
                isOpen={isDecisionModalOpen}
                onClose={closeDecisionModal}
                onSubmit={handleSubmitDecision}
                decision={selectedDecision}
              />
            </div>
          </div>
          <div className="flex flex-col">
            <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                <div className="overflow-hidden">
                  <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title={`Success`}></Modal>
                  <table {...getTableProps()} className="mt-3 w-full table-auto border-collapse bg-white rounded-sm border-b-2">
                    <thead>
                      {headerGroups.map((headerGroup: any) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map((column: any) => (
                            <th {...column.getHeaderProps()} align="left" className="border-slate-100 uppercase text-sm text-gray-800 font-semibold py-6 px-4 border-b-2">
                              {column.render("Header")}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {decisions.length ? (
                        page.map((row: any, i: number) => {
                          prepareRow(row);
                          return (
                            <DecisionProfile
                              key={i}
                              decision={row.original}
                              isSelected={selectedRows.findIndex((decision) => decision.id === row.original.id) !== -1}
                              onClick={() => toggleSelect(row.original)}
                              onUpdate={() => {
                                setSelectedDecision(row.original);
                                openDecisionModal();
                              }}
                              onDelete={(id: string) => {
                                DeleteDecision(farmUrl, farmKey, id).then(updateDecisions);
                              }}
                            />
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={10} align="center" className="p-20 text-xs">
                            No Profiles available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <Pagination pageIndex={pageIndex} pageSize={pageSize} setPageSize={setPageSize} gotoPage={gotoPage} rowCount={rows.length} extend />
                </div>
              </div>
            </div>
          </div>
        </section>
      }
    </div>
  );
}
